"use client"

import { useState, useEffect } from "react"
import DashboardTemplate from "../templates/DashboardTemplate"
import api from "../../services/api"
import { useAuth } from "../../hooks/useAuth"
import Card from "../molecules/Card"
import {
  BarChart2,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  Coffee,
  Users,
  DollarSign,
  ShoppingBag,
  ArrowUpRight,
  Menu,
} from "lucide-react"
import { Link } from "react-router-dom"

const Spinner = () => (
  <div className="flex justify-center items-center py-8">
    <svg
      className="animate-spin h-10 w-10 text-amber-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
  </div>
)

const StatusBadge = ({ status }) => {
  const getStatusConfig = () => {
    switch (status) {
      case "Pending":
        return { icon: Clock, color: "bg-amber-100 text-amber-800 border-amber-200" }
      case "Accepted":
        return { icon: CheckCircle, color: "bg-blue-100 text-blue-800 border-blue-200" }
      case "In Progress":
        return { icon: Coffee, color: "bg-purple-100 text-purple-800 border-purple-200" }
      case "Ready":
        return { icon: ClipboardList, color: "bg-green-100 text-green-800 border-green-200" }
      case "Completed":
        return { icon: CheckCircle, color: "bg-green-100 text-green-800 border-green-200" }
      case "Cancelled":
        return { icon: XCircle, color: "bg-red-100 text-red-800 border-red-200" }
      default:
        return { icon: Clock, color: "bg-gray-100 text-gray-800 border-gray-200" }
    }
  }

  const { icon: Icon, color } = getStatusConfig()

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${color}`}>
      <Icon className="w-3.5 h-3.5 mr-1" />
      {status}
    </span>
  )
}

const ClipboardList = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1z" />
    <path d="M9 12h6" />
    <path d="M9 16h6" />
    <path d="M9 8h1" />
  </svg>
)

const StatCard = ({ icon: Icon, title, value, change, linkTo }) => (
  <Card className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
        {change && (
          <p
            className={`mt-1 text-xs flex items-center ${
              change > 0 ? "text-green-600" : change < 0 ? "text-red-600" : "text-gray-500"
            }`}
          >
            <ArrowUpRight className={`h-3 w-3 mr-1 ${change < 0 ? "rotate-180" : ""}`} />
            {Math.abs(change)}% from last week
          </p>
        )}
      </div>
      <div className="p-2 bg-amber-100 rounded-lg">
        <Icon className="h-5 w-5 text-amber-600" />
      </div>
    </div>
    {linkTo && (
      <div className="mt-4 pt-3 border-t border-gray-100">
        <Link to={linkTo} className="text-sm font-medium text-amber-600 hover:text-amber-700 flex items-center">
          View details
          <ArrowUpRight className="h-3 w-3 ml-1" />
        </Link>
      </div>
    )}
  </Card>
)

export default function OwnerDashboardPage() {
  const { user } = useAuth()
  const ownerId = user?._id
  const [restaurant, setRestaurant] = useState(null)
  const [orders, setOrders] = useState([])
  const [stats, setStats] = useState({
    totalOrders: 0,
    revenue: 0,
    customers: 0,
    pendingOrders: 0,
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: rest } = await api.get(`/restaurants/owner/${ownerId}`)
        setRestaurant(rest)

        const { data: ords } = await api.get(`/orders/restaurant/${rest._id}`)
        setOrders(ords)

        // Calculate basic stats
        const pendingOrders = ords.filter((o) => o.status === "Pending").length
        const revenue = ords.reduce((sum, order) => {
          const orderTotal = order.orderItems.reduce((total, item) => total + (item.price || 0) * item.quantity, 0)
          return sum + orderTotal
        }, 0)

        // Get unique customers
        const uniqueCustomers = new Set(ords.map((o) => o.user?._id).filter(Boolean)).size

        setStats({
          totalOrders: ords.length,
          revenue,
          customers: uniqueCustomers,
          pendingOrders,
        })
      } catch (err) {
        setError(err.response?.data?.error || err.message)
      } finally {
        setLoading(false)
      }
    }

    if (ownerId) fetchData()
  }, [ownerId])

  if (loading)
    return (
      <DashboardTemplate>
        <Spinner />
      </DashboardTemplate>
    )

  if (error)
    return (
      <DashboardTemplate>
        <div className="p-6">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
            <p>{error}</p>
          </div>
        </div>
      </DashboardTemplate>
    )

  return (
    <DashboardTemplate>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <BarChart2 className="h-7 w-7 mr-2 text-amber-600" />
            <h1 className="text-3xl font-bold text-gray-800">Owner Dashboard</h1>
          </div>
          {restaurant && <div className="text-sm text-gray-500">{restaurant.name}</div>}
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={ShoppingBag}
            title="Total Orders"
            value={stats.totalOrders}
            change={12}
            linkTo="/order-management"
          />
          <StatCard
            icon={DollarSign}
            title="Revenue"
            value={`$${stats.revenue.toFixed(2)}`}
            change={8}
            linkTo="/analytics-recommendations"
          />
          <StatCard icon={Users} title="Customers" value={stats.customers} change={5} />
          <StatCard icon={Clock} title="Pending Orders" value={stats.pendingOrders} linkTo="/order-management" />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/menu-management"
              className="bg-white border border-gray-200 rounded-lg p-4 flex items-center hover:bg-amber-50 transition-colors"
            >
              <div className="p-2 bg-amber-100 rounded-lg mr-3">
                <Menu className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Manage Menu</h3>
                <p className="text-sm text-gray-500">Add or edit menu items</p>
              </div>
            </Link>
            <Link
              to="/order-management"
              className="bg-white border border-gray-200 rounded-lg p-4 flex items-center hover:bg-amber-50 transition-colors"
            >
              <div className="p-2 bg-amber-100 rounded-lg mr-3">
                <ClipboardList className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Manage Orders</h3>
                <p className="text-sm text-gray-500">View and update orders</p>
              </div>
            </Link>
            <Link
              to="/restaurant-management"
              className="bg-white border border-gray-200 rounded-lg p-4 flex items-center hover:bg-amber-50 transition-colors"
            >
              <div className="p-2 bg-amber-100 rounded-lg mr-3">
                <Coffee className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Restaurant Profile</h3>
                <p className="text-sm text-gray-500">Update your restaurant details</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Orders */}
        <div>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Orders</h2>
          {orders.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
              <ClipboardList className="mx-auto h-12 w-12 text-amber-500 mb-2" />
              <h3 className="text-lg font-medium text-gray-900">No orders yet</h3>
              <p className="mt-1 text-sm text-gray-500">Orders will appear here once customers place them.</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {orders.slice(0, 6).map((order) => (
                <Card
                  key={order._id}
                  header={
                    <div className="flex justify-between items-center">
                      <h3 className="font-semibold text-lg">Order #{order._id.slice(0, 8)}</h3>
                      <StatusBadge status={order.status} />
                    </div>
                  }
                  footer={
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">
                        {new Date(order.createdAt || Date.now()).toLocaleString()}
                      </span>
                      <Link to="/order-management" className="text-amber-600 hover:text-amber-700 text-sm font-medium">
                        Manage
                      </Link>
                    </div>
                  }
                  className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="space-y-2">
                    <p className="flex items-center">
                      <span className="font-medium mr-2">Customer:</span>
                      {order.user?.username || "Anonymous"}
                    </p>
                    <p className="flex items-center">
                      <span className="font-medium mr-2">Type:</span>
                      {order.orderType === "delivery" ? (
                        <span className="flex items-center">
                          <Truck className="h-4 w-4 mr-1 text-amber-600" /> Delivery
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <Coffee className="h-4 w-4 mr-1 text-amber-600" /> Dine-in
                        </span>
                      )}
                    </p>
                    <div>
                      <span className="font-medium">Items:</span>
                      <ul className="mt-1 space-y-1">
                        {order.orderItems.slice(0, 3).map((oi, idx) => (
                          <li key={idx} className="text-sm pl-2 border-l-2 border-amber-200">
                            {oi.item.name} × {oi.quantity}
                          </li>
                        ))}
                        {order.orderItems.length > 3 && (
                          <li className="text-xs text-gray-500 pl-2">+{order.orderItems.length - 3} more items</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {orders.length > 6 && (
            <div className="mt-4 text-center">
              <Link
                to="/order-management"
                className="inline-flex items-center text-amber-600 hover:text-amber-700 font-medium"
              >
                View all orders
                <ArrowUpRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </DashboardTemplate>
  )
}
